import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  Avatar,
  Fade,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  LockOutlined as LockIcon,
  EmailOutlined as EmailIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { LogoImage } from "../components/layout/Logo";

const ForgotPassword: React.FC = () => {
  const {
    requestPasswordReset,
    verifyResetCode,
    confirmPasswordReset,
    error: authError,
    loading: authLoading,
  } = useAuth();

  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Track the current step in the password reset flow
  const [activeStep, setActiveStep] = useState(0);

  // Steps in the password reset process
  const steps = ["Request Reset", "Verify Code", "Set New Password"];

  // Sync with auth context errors
  useEffect(() => {
    if (authError) {
      setError(authError);
      setLoading(false);
    }
  }, [authError]);

  // Clear error when inputs change
  useEffect(() => {
    if (error) {
      setError(null);
    }
  }, [email, resetCode, newPassword, confirmPassword]);

  // Use combined loading state
  const isLoading = loading || authLoading;

  // Form validation
  const validateEmail = (): boolean => {
    let isValid = true;

    if (!email.trim()) {
      setError("Email is required");
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      isValid = false;
    }

    return isValid;
  };

  const validateCode = (): boolean => {
    if (!resetCode.trim()) {
      setError("Verification code is required");
      return false;
    }
    return true;
  };

  const validateNewPassword = (): boolean => {
    if (!newPassword) {
      setError("Password is required");
      return false;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    return true;
  };

  const handleRequestReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateEmail()) return;

    try {
      setError(null);
      setLoading(true);
      await requestPasswordReset(email);
      setSuccess("A verification code has been sent to your email");
      setActiveStep(1);
    } catch (err) {
      // Error will be set via the useEffect that watches authError
      console.error("Request reset error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateCode()) return;

    try {
      setError(null);
      setLoading(true);
      const isValid = await verifyResetCode(resetCode);
      if (isValid) {
        setSuccess("Code verified successfully");
        setActiveStep(2);
      } else {
        setError("Invalid verification code");
      }
    } catch (err) {
      console.error("Verify code error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateNewPassword()) return;

    try {
      setError(null);
      setLoading(true);
      await confirmPasswordReset(resetCode, newPassword);
      setSuccess(
        "Your password has been successfully reset. You can now log in with your new password."
      );
    } catch (err) {
      console.error("Reset password error:", err);
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box component="form" onSubmit={handleRequestReset} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              error={!!error}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.2 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Request Password Reset"
              )}
            </Button>
          </Box>
        );
      case 1:
        return (
          <Box component="form" onSubmit={handleVerifyCode} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="resetCode"
              label="Verification Code"
              name="resetCode"
              autoFocus
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
              disabled={isLoading}
              error={!!error}
              helperText="Enter the verification code sent to your email"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.2 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Verify Code"
              )}
            </Button>
          </Box>
        );
      case 2:
        return (
          <Box component="form" onSubmit={handleResetPassword} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="newPassword"
              label="New Password"
              name="newPassword"
              type="password"
              autoFocus
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isLoading}
              error={!!error}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="confirmPassword"
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              error={!!error}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.2 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Reset Password"
              )}
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card sx={{ borderRadius: 2, overflow: "visible" }}>
        {isLoading && <LinearProgress sx={{ borderRadius: "4px 4px 0 0" }} />}
        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            <LogoImage width={120} height={120} noText={false} />
            <Typography variant="h4" component="h1" gutterBottom>
              Password Reset
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {activeStep === 0 &&
                "Enter your email to receive a password reset code"}
              {activeStep === 1 &&
                "Enter the verification code sent to your email"}
              {activeStep === 2 && "Create a new password for your account"}
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Error message with fade effect */}
          <Fade in={error !== null}>
            <div style={{ width: "100%", marginBottom: error ? "16px" : 0 }}>
              {error && (
                <Alert
                  severity="error"
                  variant="filled"
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}
            </div>
          </Fade>

          {/* Success message with fade effect */}
          <Fade in={success !== null}>
            <div style={{ width: "100%", marginBottom: success ? "16px" : 0 }}>
              {success && (
                <Alert
                  severity="success"
                  variant="filled"
                  onClose={() => setSuccess(null)}
                >
                  {success}
                </Alert>
              )}
            </div>
          </Fade>

          {renderCurrentStep()}

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>

          <Box textAlign="center">
            <Link
              component={RouterLink}
              to="/login"
              variant="body1"
              underline="hover"
            >
              Back to Sign In
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ForgotPassword;
